import JSZip from "jszip";
import type { Frame, POI } from "../api/types";
import { frameDownloadName } from "./frameFilename";

/** How many full-res JPEGs are in flight at once. */
const FETCH_WORKERS = 4;

export interface FramesZipInput {
  frames: Frame[];
  /** The flight's POIs, only so a POI-linked frame can carry the POI's name. */
  pois: POI[];
  /** Flight display timezone; omit for the runtime's local zone. */
  tz?: string;
  /** Resolve a fresh presigned GET URL for a frame's full-res JPEG. */
  frameUrl: (frameId: string) => Promise<string>;
  onProgress?: (done: number, total: number) => void;
}

export interface FramesZipResult {
  /** Null when nothing could be exported. */
  blob: Blob | null;
  exported: number;
  failures: number;
}

export function framesZipFilename(flightId: string): string {
  return `${flightId}_frames.zip`;
}

function safeNamePart(name: string): string {
  return name.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

/**
 * The filename each frame gets inside the zip, keyed by frame id: the same
 * `frame_HH_MM_SS.jpg` the single download uses, the POI's name appended for a
 * POI-linked frame, and `_2`, `_3`… for frames that would otherwise collide.
 */
export function frameExportNames(
  frames: Frame[], pois: POI[], tz?: string,
): Map<string, string> {
  const poiNames = new Map(pois.map((p) => [p.id, p.name]));
  const used = new Set<string>();
  const out = new Map<string, string>();
  for (const f of frames) {
    let stem = frameDownloadName(f, tz).replace(/\.jpg$/, "");
    const poiPart = f.poiId !== undefined ? safeNamePart(poiNames.get(f.poiId) ?? "") : "";
    if (poiPart !== "") stem = `${stem}_${poiPart}`;
    let name = `${stem}.jpg`;
    for (let n = 2; used.has(name); n += 1) name = `${stem}_${n}.jpg`;
    used.add(name);
    out.set(f.id, name);
  }
  return out;
}

/**
 * Zip the full-res JPEG of every frame. One frame failing never sinks the
 * export: it is left out and counted in `failures`. The bytes are the stored
 * (EXIF-tagged) uploads, untouched; JPEGs are stored uncompressed because
 * deflating them buys nothing.
 */
export async function buildFramesZip(input: FramesZipInput): Promise<FramesZipResult> {
  const { frames } = input;
  const names = frameExportNames(frames, input.pois, input.tz);
  const zip = new JSZip();
  let exported = 0;
  let failures = 0;
  let done = 0;
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < frames.length) {
      const f = frames[next] as Frame;
      next += 1;
      try {
        if (!f.hasFull) throw new Error("no full-res image");
        const res = await fetch(await input.frameUrl(f.id));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        zip.file(names.get(f.id) as string, await res.blob());
        exported += 1;
      } catch {
        failures += 1;
      }
      done += 1;
      input.onProgress?.(done, frames.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(FETCH_WORKERS, frames.length) }, worker));

  if (exported === 0) return { blob: null, exported, failures };
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  return { blob, exported, failures };
}
